import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SideNav from './helpers/SideNavbar';
import Select from 'react-select';
import { Link, useParams, useNavigate } from 'react-router-dom';
import TimezoneSelect from 'react-timezone-select';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faChartBar, faLineChart, faNewspaper } from '@fortawesome/free-solid-svg-icons';

const EditSchedulePost = () => {
    const { PostId } = useParams();
    const [selectedDays, setSelectedDays] = useState([]);
    const [time, setTime] = useState('');
    const [date, setDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState([]);
    const [selectedPrompt, setSelectedPrompt] = useState(null); // Update to handle single select
    const [postInfo, setPostInfo] = useState(null);
    const [selectedTimezone, setSelectedTimezone] = useState({}); // State for timezone

    const UserID = localStorage.getItem("UserID");
    const tokenID = localStorage.getItem('Tokenid');
    const userProfile = JSON.parse(localStorage.getItem("userProfile"));
    const AccountId = userProfile ? userProfile[0].AccountId : null;
    const navigate = useNavigate();

    const daysOfWeek = [
        { value: 'Monday', label: 'Monday' },
        { value: 'Tuesday', label: 'Tuesday' },
        { value: 'Wednesday', label: 'Wednesday' },
        { value: 'Thursday', label: 'Thursday' },
        { value: 'Friday', label: 'Friday' },
        { value: 'Saturday', label: 'Saturday' },
        { value: 'Sunday', label: 'Sunday' }
    ];

    useEffect(() => {
        if (UserID) {
            setLoading(true)
            axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getprompts/${UserID}`)
                .then((response) => {
                    console.log("Prompts API Response:", response.data);
                    setLoading(false);
                    const fetchedPrompts = response.data;
                    const promptOptions = fetchedPrompts.map((prompt) => ({
                        value: prompt.PromptID || prompt.promptID || prompt.PromptId, // Handle different field names
                        label: prompt.PromptData || prompt.promptData || prompt.Prompt || prompt.prompt
                    }));
                    console.log("Processed prompt options:", promptOptions);
                    setOptions(promptOptions);
                    setLoading(false);
                })
                .catch((error) => {
                    console.error('Error fetching prompts:', error);
                    setLoading(false);
                });
        }
    }, [UserID]);

    useEffect(() => {
        const fetchPostInfo = async () => {
            setLoading(true);

            try {
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getscheduledpostsbyPostId/${PostId}`);
                setLoading(false)
                if (response.data) {
                    const postInfo = response.data.find(post => post.PostId == PostId) || response.data[0];
                    if (postInfo) {
                        console.log('Post Info:', postInfo); // Debug log
                        setPostInfo(postInfo);
                        setEmail(postInfo.Email); // Keep email in state for backend, but don't show in UI
                        setDate(postInfo.StartDate?.split('T')[0]); 
                        setEndDate(postInfo.EndDate?.split('T')[0]);
                        const dateTimeString = postInfo.ScheduledTimeAsPerZone;
                        const dateTime = new Date(dateTimeString);
                        const formattedTime = dateTime.toISOString().substr(11, 5);
                        setTime(formattedTime);
                        const selectedDayValues = postInfo.Days.split(',').map(day => daysOfWeek.find(d => d.value === day.trim()));
                        setSelectedDays(selectedDayValues.filter(d => d !== undefined));
                        setSelectedTimezone({ value: postInfo.TimeZone, label: postInfo.TimeZone });
                    } else {
                        setError('Post not found');
                    }
                } else {
                    setError('No post data available');
                }
            } catch (error) {
                console.error('Error fetching post info:', error);
                setError('Failed to fetch post info');
            }
        };
        fetchPostInfo();
    }, [PostId]);

    // Set selected prompt once both postInfo and options are available
    useEffect(() => {
        if (postInfo && options.length > 0) {
            console.log('Matching prompt - postInfo:', postInfo, 'options:', options, 'current selectedPrompt:', selectedPrompt); // Debug log
            let matchedPrompt = null;
            
            // Get prompt text - check both Prompt and PromptID fields
            // For create-post-schedule, prompt text might be in PromptID field instead of Prompt
            let promptText = postInfo.Prompt || postInfo.prompt || postInfo.Prompts || postInfo.prompts;
            const promptIdField = postInfo.PromptID || postInfo.promptID || postInfo.promptId || postInfo.PromptId;
            
            // If Prompt is null/empty but PromptID contains text (not numeric), use PromptID as prompt text
            if ((!promptText || promptText === null) && promptIdField && typeof promptIdField === 'string' && !/^\d+$/.test(promptIdField.trim())) {
                promptText = promptIdField;
                console.log('Using PromptID as prompt text (create-post-schedule case):', promptText);
            }
            
            // If we have prompt text, try to match it with options
            if (promptText) {
                const firstPrompt = typeof promptText === 'string' ? promptText.split('|')[0].trim() : String(promptText);
                
                // First, check if it's a numeric ID
                if (/^\d+$/.test(firstPrompt)) {
                    const numericId = Number(firstPrompt);
                    matchedPrompt = options.find(opt => 
                        opt.value === numericId || 
                        Number(opt.value) === numericId ||
                        String(opt.value) === String(numericId)
                    );
                    console.log('Tried matching numeric ID:', numericId, 'Found:', matchedPrompt);
                } else {
                    // It's text, try to match by label
                    // Try exact match first
                    matchedPrompt = options.find(opt => {
                        const optLabel = String(opt.label || '').trim();
                        const searchText = firstPrompt.trim();
                        return optLabel === searchText;
                    });
                    // If no exact match, try partial match
                    if (!matchedPrompt) {
                        matchedPrompt = options.find(opt => {
                            const optLabel = String(opt.label || '').trim().toLowerCase();
                            const searchText = firstPrompt.trim().toLowerCase();
                            return optLabel.includes(searchText) || searchText.includes(optLabel);
                        });
                    }
                    console.log('Tried matching by text:', firstPrompt, 'Found:', matchedPrompt);
                }
            }
            
            // If still not found, try matching by numeric PromptID (if it's numeric)
            if (!matchedPrompt && promptIdField && /^\d+$/.test(String(promptIdField).trim())) {
                const numericId = Number(promptIdField);
                matchedPrompt = options.find(opt => 
                    opt.value === numericId || 
                    Number(opt.value) === numericId ||
                    String(opt.value) === String(numericId)
                );
                console.log('Tried matching by numeric PromptID:', numericId, 'Found:', matchedPrompt);
            }
            
            // If still not found, create a new option from the prompt text
            if (!matchedPrompt && promptText) {
                const firstPrompt = typeof promptText === 'string' ? promptText.split('|')[0].trim() : String(promptText);
                const promptId = promptIdField || firstPrompt;
                matchedPrompt = { value: promptId, label: firstPrompt };
                console.log('Created new prompt option:', matchedPrompt);
                // Add to options if it's a valid prompt
                setOptions(prev => {
                    // Check if already exists to avoid duplicates
                    const exists = prev.find(opt => 
                        String(opt.value) === String(promptId) || 
                        opt.label === firstPrompt
                    );
                    return exists ? prev : [...prev, matchedPrompt];
                });
            }
            
            // Only update if we found a match and it's different from current
            if (matchedPrompt && (!selectedPrompt || selectedPrompt.value !== matchedPrompt.value)) {
                console.log('Setting selected prompt:', matchedPrompt); // Debug log
                setSelectedPrompt(matchedPrompt);
            } else if (!matchedPrompt) {
                console.warn('Could not match prompt for postInfo:', postInfo); // Debug log
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postInfo, options]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (selectedDays.length === 0 || !time || !selectedPrompt || !date || !endDate) {
            setError('Please provide all fields');
            return;
        }
        const accessToken = localStorage.getItem('AccessToken');
        if (!accessToken || !UserID) {
            setError('Access token or user ID is missing');
            return;
        }
        if (new Date(endDate) < new Date(date)) {
            setError('End date must be greater than or equal to start date');
            return;
        }
        const daysString = selectedDays.map(day => day.value).join(',');
        const prompt = selectedPrompt.value;
        setLoading(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/updatescheduledpostsbyPostId`, {
                PostId: Number(PostId),
                Days: daysString,
                ScheduleTime: time,
                accessToken,
                tokenID,
                StartDate: date,
                EndDate: endDate,
                Prompt: prompt,
                TimeZone: selectedTimezone.value,
                Email: email,
                AccountId,
                Status: "PENDING",
                UserID: UserID,
                PromptType: 1
            });
            setMessage(response.data.message);
            if (response.data.message === 'Post updated successfully') {
                Swal.fire({
                    position: 'center',
                    icon: 'success',
                    title: 'Post updated successfully',
                    showConfirmButton: false,
                    timer: 3000
                });
            } else {
                Swal.fire({
                    position: 'center',
                    icon: 'error',
                    title: 'Error updating post!',
                    showConfirmButton: false,
                    timer: 3000
                });
            }
            setError('');
        } catch (error) {
            console.error('Error editing schedule post:', error);
            setError('Failed to edit the schedule post');
            setMessage('');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectChange = (selectedOptions) => {
        setSelectedDays(selectedOptions || []);
    };

    const handleSelectPrompt = (selectedOption) => {
        setSelectedPrompt(selectedOption);
    };

    return (
        <div>
            <SideNav>
                <div className="card shadow-sm p-3" style={{ border: 'none', backgroundColor: '#fff' }}>
                    <div className="card-header d-flex justify-content-between align-items-center" style={{ border: 'none', backgroundColor: '#fff', padding: '16px' }}>
                        <div className='d-flex align-items-center'>
                            <Link to="/schedule-list">
                                <h5 className="welcome-text mb-0" style={{ fontSize: '20px', color: '#2e3192', textAlign: 'left', margin: 0 }}>
                                    <i className="fa fa-arrow-left" style={{ marginRight: '30px' }} aria-hidden="true"></i>Edit Schedule
                                </h5>
                            </Link>
                        </div>
                        
                        {/* Navigation Icons */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <button
                                onClick={() => navigate('/viewallpendingposts')}
                                title="View Pending Posts"
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    borderRadius: '8px',
                                    color: '#2e3192',
                                    transition: 'all 0.3s ease',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f3f2ef';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '20px' }} />
                                <span style={{ fontSize: '11px', fontWeight: '500' }}>Pending</span>
                            </button>
                            
                            <button
                                onClick={() => navigate('/viewallapprovedposts')}
                                title="View Approved Posts"
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    borderRadius: '8px',
                                    color: '#2e3192',
                                    transition: 'all 0.3s ease',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f3f2ef';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <FontAwesomeIcon icon={faChartBar} style={{ fontSize: '20px' }} />
                                <span style={{ fontSize: '11px', fontWeight: '500' }}>Approved</span>
                            </button>
                            
                            <button
                                onClick={() => navigate('/schedule-list')}
                                title="View Schedule List"
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    borderRadius: '8px',
                                    color: '#2e3192',
                                    transition: 'all 0.3s ease',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f3f2ef';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <FontAwesomeIcon icon={faLineChart} style={{ fontSize: '20px' }} />
                                <span style={{ fontSize: '11px', fontWeight: '500' }}>Schedule</span>
                            </button>
                            
                            <button
                                onClick={() => navigate('/viewallpostedposts')}
                                title="View Published Posts"
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    borderRadius: '8px',
                                    color: '#2e3192',
                                    transition: 'all 0.3s ease',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f3f2ef';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <FontAwesomeIcon icon={faNewspaper} style={{ fontSize: '20px' }} />
                                <span style={{ fontSize: '11px', fontWeight: '500' }}>Published</span>
                            </button>
                        </div>
                    </div>
                    <hr />
                    {loading ? <div className='spinner'></div> :
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-sm-12 col-lg-6 col-xl-6 col-md-6">
                                    <div>
                                        <label style={{ textAlign: 'left', marginTop: '15px' }}>Days of the Week:</label>
                                        <Select
                                            options={daysOfWeek}
                                            value={selectedDays}
                                            onChange={handleSelectChange}
                                            isDisabled={true}
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-12 col-lg-6 col-xl-6 col-md-6">
                                    <div>
                                        <label style={{ textAlign: 'left', marginTop: '15px' }}>Time:</label>
                                        <input
                                            type="time"
                                            value={time}
                                            onChange={(e) => setTime(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-sm-12 col-lg-6 col-xl-6 col-md-6">
                                    <div>
                                        <label style={{ textAlign: 'left', marginTop: '15px' }}>Start Date:</label>
                                        <input
                                            type="date"
                                            value={date}
                                            onChange={(e) => setDate(e.target.value)}
                                            disabled
                                            style={{ opacity: 0.6, cursor: 'not-allowed' }}
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-12 col-lg-6 col-xl-6 col-md-6">
                                    <div>
                                        <label style={{ textAlign: 'left', marginTop: '15px' }}>End Date:</label>
                                        <input
                                            type="date"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                            disabled
                                            style={{ opacity: 0.6, cursor: 'not-allowed' }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-sm-12 col-lg-6 col-xl-6 col-md-6">
                                    <div>
                                        <label style={{ textAlign: 'left', marginTop: '15px' }}>Prompts:</label>
                                        <Select
                                            options={options}
                                            value={selectedPrompt}
                                            onChange={handleSelectPrompt}
                                            isSearchable
                                            placeholder="Select a prompt"
                                            isDisabled={true}
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-12 col-lg-6 col-xl-6 col-md-6">
                                    <div>
                                        <label style={{ textAlign: 'left', marginTop: '15px' }}>Timezone:</label>
                                        <TimezoneSelect
                                            value={selectedTimezone}
                                            onChange={setSelectedTimezone}
                                            isDisabled={true}
                                        />
                                    </div>
                                </div>
                            </div>
                            <hr />
                            {error && <div className="error">{error}</div>}
                            {message && <div className="message">{message}</div>}
                            <button type="submit" className="btn btn-primary" disabled={loading}>
                                Update Schedule
                            </button>
                        </form>
                    }
                </div>
            </SideNav>
        </div>
    );
};

export default EditSchedulePost;
